import { ScrollArea } from "@/components/ui/scroll-area";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { Expense } from "@/types/expense";
import { TripCard } from "../history/TripCard";
import { Card } from "@/components/ui/card";
import { HistoryHeader } from "../history/HistoryHeader";
import { toast } from "sonner";
import { History as HistoryIcon, Filter, SortAsc, SortDesc } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface HistoryTabProps {
  expenses: Expense[];
  tripParticipants: string[];
}

export const HistoryTab = ({ expenses }: HistoryTabProps) => {
  const [selectedTrip, setSelectedTrip] = useState<string | null>(null);
  const [completedTrips, setCompletedTrips] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [filterBy, setFilterBy] = useState<"all" | "date" | "amount">("all");

  useEffect(() => {
    const loadTripHistory = () => {
      const savedHistory = localStorage.getItem('tripHistory');
      if (savedHistory) {
        const parsedHistory = JSON.parse(savedHistory);
        setCompletedTrips(parsedHistory);
      }
    };

    loadTripHistory();
    window.addEventListener('storage', loadTripHistory);
    return () => window.removeEventListener('storage', loadTripHistory);
  }, []);

  const handleExport = () => {
    toast.success("Exporting trip history...");
  };

  const filteredAndSortedTrips = completedTrips
    .filter(trip => 
      trip.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      trip.expenses.some((exp: any) => 
        exp.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    )
    .sort((a, b) => {
      if (filterBy === "date") {
        return sortOrder === "asc" 
          ? new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
          : new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
      } else if (filterBy === "amount") {
        return sortOrder === "asc" 
          ? a.totalAmount - b.totalAmount
          : b.totalAmount - a.totalAmount;
      }
      return 0;
    });

  if (completedTrips.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="space-y-6"
      >
        <HistoryHeader onExport={handleExport} />
        <Card className="p-8 text-center space-y-4 bg-gradient-to-br from-gray-50 to-gray-100">
          <HistoryIcon className="h-12 w-12 text-primary mx-auto opacity-50" />
          <h3 className="text-xl font-semibold">No Completed Trips Yet</h3>
          <p className="text-muted-foreground">
            Your completed trips will appear here after you end them.
          </p>
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      <HistoryHeader onExport={handleExport} />
      
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex-1">
          <Input
            placeholder="Search trips..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full"
          />
        </div>
        <div className="flex gap-2">
          <Select value={filterBy} onValueChange={(value: any) => setFilterBy(value)}>
            <SelectTrigger className="w-[180px]">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Filter by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="date">Date</SelectItem>
              <SelectItem value="amount">Amount</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={() => setSortOrder(prev => prev === "asc" ? "desc" : "asc")}
            className="flex items-center gap-2"
          >
            {sortOrder === "asc" ? (
              <SortAsc className="h-4 w-4" />
            ) : (
              <SortDesc className="h-4 w-4" />
            )}
            Sort
          </Button>
        </div>
      </div>

      <ScrollArea className="h-[calc(100vh-16rem)] rounded-lg">
        <div className="space-y-6">
          {filteredAndSortedTrips.map((trip) => (
            <TripCard
              key={trip.startDate}
              trip={trip}
              selectedTrip={selectedTrip}
              onTripSelect={setSelectedTrip}
            />
          ))}
        </div>
      </ScrollArea>
    </motion.div>
  );
};